import { useEffect, useState, type ReactNode } from "react";
import { css } from "@emotion/react";
import CheckIcon from "@mui/icons-material/Check";
import SyncProblem from "@mui/icons-material/SyncProblem";
import type { ButtonProps } from "@mui/material";
import { alpha, Box, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

import { useScreenDetection } from "../../hooks/ScreenDetection/useScreenDetection.js";
import { breakpoints } from "../../styles/themeCommon.js";
import type { ButtonIcon } from "../design-system/Button/Button.js";
import { Button } from "../design-system/Button/Button.js";
import { FancyIcon } from "../design-system/FancyIcon.js";
import { LoadingSpinner } from "../design-system/LoadingSpinner.js";
import type { AutoSaveState } from "../types/form.js";

export interface FormFooterProps {
  onSave: (params: any) => void;
  onCancel: (params: any) => void;
  saveLabel?: ButtonProps["aria-label"];
  discardLabel?: ButtonProps["aria-label"];
  discardIcon?: ButtonIcon;
  discardColor?: ButtonProps["color"];
  saveIcon?: ButtonIcon;
  disableSubmit?: ButtonProps["disabled"];
  disableDiscard?: ButtonProps["disabled"];
  secondaryLeftButton?: ReactNode;
  secondaryRightButton?: ReactNode;
  hideSubmit?: boolean;
  hideDiscard?: boolean;
  saveVariant?: ButtonProps["variant"];
  minHeight?: boolean;
  startIcon?: ButtonIcon;
  isSticky?: boolean;
  isForcedSticky?: boolean;
  autoSaveState?: AutoSaveState;
  performSaveActionOnFormSubmit?: boolean;
  autoSaveType?: string;
  customFooterBackgroundColor?: string;
  children?: ReactNode;
}

const defaultProps: Partial<FormFooterProps> = {
  saveLabel: "SAVE AND CONTINUE",
  discardLabel: "SAVE AS DRAFT",
  discardIcon: undefined,
  discardColor: "primary",
  disableSubmit: false,
  disableDiscard: false,
  saveIcon: undefined,
  secondaryLeftButton: undefined,
  secondaryRightButton: undefined,
  hideSubmit: false,
  hideDiscard: false,
  saveVariant: "contained",
  minHeight: false,
  startIcon: undefined,
  isSticky: false,
  isForcedSticky: false,
  performSaveActionOnFormSubmit: false,
};
export const FormFooterSection = (props: FormFooterProps) => {
  const theme = useTheme();

  const allProps = { ...defaultProps, ...props };

  const { isMobileView } = useScreenDetection();

  const {
    onSave,
    onCancel,
    saveLabel,
    discardLabel,
    discardIcon,
    discardColor,
    saveIcon,
    disableSubmit,
    disableDiscard,
    secondaryLeftButton,
    secondaryRightButton,
    hideSubmit,
    hideDiscard,
    saveVariant,
    minHeight,
    startIcon,
    isSticky,
    isForcedSticky,
    autoSaveState,
    performSaveActionOnFormSubmit,
    autoSaveType,
    customFooterBackgroundColor,
    children,
  } = allProps;

  const [height, setHeight] = useState(0);

  useEffect(() => {
    const root = document.querySelector(".MuiBox-root")!;
    const height = (root as HTMLElement).offsetHeight;

    setHeight(height);
  }, []);

  const styles = {
    footerContainer: css`
      ${isSticky &&
      (height > 750 || isForcedSticky) &&
      !isMobileView &&
      `position: sticky; 
      bottom: 0; `}
      z-index: 999;
      margin: ${theme.spacing(0, -6)};
      display: flex;
      border-top: 1px solid ${alpha(theme.palette.text.primary, 0.05)};
      background-color: ${customFooterBackgroundColor ||
      theme.palette.secondary.light};
      justify-content: space-between;
      align-items: center;
      min-height: ${minHeight && (secondaryLeftButton || hideDiscard)
        ? theme.spacing(14)
        : ""};
      padding-top: ${theme.spacing(2)};
      padding-bottom: ${theme.spacing(2)};
      @media (max-width: ${breakpoints.values.sm}px) {
        background-color: ${theme.palette.common.white};
        display: flex;
        flex-wrap: wrap;
        margin: 0;
        padding: 0;
      }
    `,
    buttonGroup: css`
      @media (max-width: ${breakpoints.values.sm}px) {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;

        justify-content: flex-start;
        width: 100%;
        margin: ${theme.spacing(1, 0)};
      }
      margin: ${theme.spacing(0, 6)};
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: ${theme.spacing(2)};
      background-color: ${theme.palette.secondary.light};
    `,
  };

  return (
    <Box css={styles.footerContainer} data-testid="form-footer">
      <Box css={styles.buttonGroup}>
        {!hideDiscard && (
          <Button
            testId="secondary-button"
            sx={{ backgroundColor: "common.white" }}
            color={discardColor}
            fullWidth={isMobileView}
            label={discardLabel}
            variant="outlined"
            onClick={onCancel}
            disabled={disableDiscard}
            {...(discardIcon && { startIcon: discardIcon })}
          />
        )}
        {secondaryLeftButton && secondaryLeftButton}
      </Box>

      <Box
        css={css`
          flex: 1;
        `}
      />
      {children}
      <Box css={styles.buttonGroup} justifyContent="flex-end">
        {autoSaveState?.hasAttemptedSave && (
          <Box sx={{ backgroundColor: "secondary.light" }}>
            <AutoSaveIndicator {...autoSaveState} autoSaveType={autoSaveType} />
          </Box>
        )}
        {secondaryRightButton && secondaryRightButton}
        {!hideSubmit && (
          <Button
            testId="primary-button"
            type={performSaveActionOnFormSubmit ? "submit" : "button"}
            fullWidth={isMobileView}
            label={saveLabel}
            variant={saveVariant}
            {...(saveIcon && { endIcon: saveIcon })}
            {...(startIcon && { startIcon })}
            onClick={onSave}
            disabled={disableSubmit}
          />
        )}
      </Box>
    </Box>
  );
};

const AutoSaveIndicator = ({
  isLoading,
  error,
  lastSavedAt,
  autoSaveType,
}: AutoSaveState) => {
  const theme = useTheme();
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <LoadingSpinner
        color={theme.palette.text.lightGray}
        label={t("pages.formPage.autoSaveStatusDraft.saveInProgress", {
          type: autoSaveType ? "profile" : "draft",
        })}
      />
    );
  }

  return (
    <Box display="flex" flexDirection="column">
      {error ? (
        <FancyIcon
          icon={SyncProblem}
          iconColor={theme.palette.error.main}
          labelColor={theme.palette.error.main}
          label={t("pages.formPage.autoSaveStatusDraft.saveError", {
            type: autoSaveType ? "Profile" : "Draft",
          })}
        />
      ) : (
        <FancyIcon
          icon={CheckIcon}
          iconColor={theme.palette.primary.lightGreen}
          labelColor={theme.palette.text.lightGray}
          label={t("pages.formPage.autoSaveStatusDraft.saveSuccess", {
            type: autoSaveType ? "Profile" : "Draft",
          })}
        />
      )}
      {lastSavedAt && (
        <Typography
          fontSize="14px"
          lineHeight={1}
          color={theme.palette.text.lightGray}
        >
          Last saved at {lastSavedAt.format("HH:mm")}
        </Typography>
      )}
    </Box>
  );
};
